import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const location = useLocation();
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setAboutOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isAboutSection = ['/about', '/calgary', '/vancouver'].includes(location.pathname);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-white/80 backdrop-blur-lg shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/">
            <motion.div
              className="text-2xl font-bold tracking-tight text-dark-slate"
              whileHover={{ scale: 1.02 }}
            >
              North
            </motion.div>
          </Link>

          {/* Nav Links & CTA */}
          <div className="flex items-center gap-6">
            <Link
              to="/services"
              className={`font-medium text-sm transition-colors ${
                location.pathname === '/services'
                  ? 'text-dark-slate'
                  : 'text-muted-grey hover:text-dark-slate'
              }`}
            >
              Services
            </Link>
            <Link
              to="/solutions"
              className={`font-medium text-sm transition-colors ${
                location.pathname === '/solutions'
                  ? 'text-dark-slate'
                  : 'text-muted-grey hover:text-dark-slate'
              }`}
            >
              Solutions
            </Link>

            {/* About Dropdown */}
            <div className="relative" ref={aboutRef}>
              <button
                onClick={() => setAboutOpen(!aboutOpen)}
                className={`font-medium text-sm transition-colors inline-flex items-center gap-1 ${
                  isAboutSection
                    ? 'text-dark-slate'
                    : 'text-muted-grey hover:text-dark-slate'
                }`}
              >
                About
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${aboutOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {aboutOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
                  >
                    <div className="py-2">
                      <Link
                        to="/about"
                        className="block px-4 py-2.5 text-sm text-dark-slate hover:bg-gray-50 transition-colors font-medium"
                      >
                        About North
                      </Link>
                      <div className="mx-4 my-1 border-t border-gray-100" />
                      <p className="px-4 pt-2 pb-1 text-xs text-muted-grey font-medium uppercase tracking-wider">Regions</p>
                      <Link
                        to="/calgary"
                        className="block px-4 py-2.5 text-sm text-muted-grey hover:text-dark-slate hover:bg-gray-50 transition-colors"
                      >
                        Calgary & Alberta
                      </Link>
                      <Link
                        to="/vancouver"
                        className="block px-4 py-2.5 text-sm text-muted-grey hover:text-dark-slate hover:bg-gray-50 transition-colors"
                      >
                        Vancouver & BC
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/blog"
              className={`font-medium text-sm transition-colors ${
                location.pathname.startsWith('/blog')
                  ? 'text-dark-slate'
                  : 'text-muted-grey hover:text-dark-slate'
              }`}
            >
              Blog
            </Link>
            <motion.a
              href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2.5 bg-dark-slate text-white rounded-full font-medium text-sm hover:bg-deep-charcoal transition-colors inline-block"
            >
              Book a Strategy Call
            </motion.a>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
